import React, {lazy, Suspense, useEffect, useState} from 'react';
import {BrowserRouter as Router, NavLink, Route, Routes,} from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Container,
    CssBaseline,
    IconButton,
    LinearProgress,
    Stack,
    ThemeProvider,
    Toolbar,
    Typography,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import {NotificationProvider} from './contexts/NotificationContext';
import {getTheme} from './theme';

// Lazy-loaded route components for code splitting
const HomePage = lazy(() => import('./components/HomePage'));
const ServerDashboard = lazy(() => import('./components/ServerDashboard'));
const ModHub = lazy(() => import('./components/ModHub'));

// Loading fallback for lazy-loaded routes
function PageLoader() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 3,
            }}
        >
            <LinearProgress sx={{width: 200, borderRadius: 4, height: 6}}/>
            <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                Loading Panel...
            </Typography>
        </Box>
    );
}

function TopBar({darkMode, onToggleTheme}) {
    return (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                background: darkMode
                    ? 'rgba(7, 11, 25, 0.8)'
                    : 'rgba(243, 252, 244, 0.8)',
                backdropFilter: 'blur(16px)',
                borderBottom: '1px solid',
                borderColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            }}
        >
            <Container maxWidth="lg">
                <Toolbar disableGutters sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Box
                        component={NavLink}
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            textDecoration: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                fontSize: 28,
                                background: 'linear-gradient(135deg, #00e676, #ff9100)',
                                borderRadius: '10px',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 15px rgba(0,230,118,0.3)',
                            }}
                        >
                            ⛏️
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 900,
                                letterSpacing: '-0.5px',
                                background: 'linear-gradient(135deg, #00e676 30%, #ff9100 90%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            MinePlay
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            component={NavLink}
                            to="/modhub"
                            variant="text"
                            sx={{
                                color: darkMode ? '#f1f5f9' : '#092518',
                                '&.active': {
                                    color: 'primary.main',
                                    background: darkMode
                                        ? 'rgba(0,230,118,0.1)'
                                        : 'rgba(0,200,83,0.1)',
                                },
                            }}
                        >
                            🛒 Mod Marketplace
                        </Button>
                        <IconButton
                            size="large"
                            color="inherit"
                            onClick={onToggleTheme}
                            sx={{color: darkMode ? '#ff9100' : '#4e6559'}}
                        >
                            {darkMode ? <Brightness7Icon/> : <Brightness4Icon/>}
                        </IconButton>
                    </Stack>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

export default function App() {
    const [darkMode, setDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme_mode');
        return saved !== null ? saved === 'dark' : true;
    });

    useEffect(() => {
        localStorage.setItem('theme_mode', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    // useMemo here is safe because the theme is derived solely from darkMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const theme = React.useMemo(() => getTheme(darkMode), [darkMode]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <NotificationProvider>
                <Router>
                    <TopBar
                        darkMode={darkMode}
                        onToggleTheme={() => setDarkMode(!darkMode)}
                    />
                    <Box
                        sx={{
                            minHeight: 'calc(100vh - 64px)',
                            background: darkMode
                                ? 'radial-gradient(circle at 50% 10%, #151e3f 0%, #070b19 70%)'
                                : 'radial-gradient(circle at 50% 10%, #e6fcf2 0%, #f3fcf4 70%)',
                            pb: 6,
                        }}
                    >
                        <Container maxWidth="lg">
                            <Suspense fallback={<PageLoader/>}>
                                <Routes>
                                    <Route path="/" element={<HomePage/>}/>
                                    <Route
                                        path="/server/:serverName"
                                        element={<ServerDashboard/>}
                                    />
                                    <Route path="/modhub" element={<ModHub/>}/>
                                </Routes>
                            </Suspense>
                        </Container>
                    </Box>
                </Router>
            </NotificationProvider>
        </ThemeProvider>
    );
}